/**
 * DocumentList.js
 *
 * Table view for DoqSolv generated documents (DMS).
 * Fetches from GET /documents endpoint and displays:
 *  - Document name, source (UI/Workflow), status, created date
 *  - Action dropdown: Download, Email, eSign
 *
 * Props:
 *  - isAdmin: boolean — admins see all docs, clients see their own
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/apiUtils';
import { toast } from 'react-toastify';

const API_BASE = '/api/external/v1/doqsolv';
const PAGE_SIZE = 15;

// Status badge color mapping
const statusColors = {
  Generated: 'bg-gray-100 text-gray-700',
  Emailed: 'bg-blue-100 text-blue-700',
  'eSign Requested': 'bg-purple-100 text-purple-700',
};

// Source badge
const sourceBadge = {
  ui: { label: 'UI', color: 'bg-indigo-50 text-indigo-600' },
  workflow: { label: 'Workflow', color: 'bg-amber-50 text-amber-700' },
};

const DocumentList = ({ isAdmin }) => {
  const [documents, setDocuments] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);

  // Action states
  const [activeAction, setActiveAction] = useState(null); // { docId, type }
  const [emailTo, setEmailTo] = useState('');
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [signerMobile, setSignerMobile] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // ── Fetch documents ───────────────────────────────────────────────────

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiCall(`${API_BASE}/documents?limit=${PAGE_SIZE}&offset=${offset}`);
      setDocuments(res.documents || []);
      setTotal(res.total || 0);
    } catch (err) {
      toast.error(err.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [offset]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  // ── Actions ───────────────────────────────────────────────────────────

  const handleDownload = (docId, format = 'pdf') => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
    window.open(`${backendUrl}${API_BASE}/documents/${docId}/download?format=${format}`, '_blank');
  };

  const handleEmailSend = async (docId) => {
    if (!emailTo.trim()) {
      toast.warning('Please enter a recipient email');
      return;
    }
    setActionLoading(true);
    try {
      await apiCall(`${API_BASE}/integrations/email`, {
        method: 'POST',
        data: { document_id: docId, recipient_email: emailTo.trim() },
      });
      toast.success('Document emailed successfully!');
      setActiveAction(null);
      setEmailTo('');
      fetchDocuments(); // Refresh to show updated status
    } catch (err) {
      toast.error(err.message || 'Failed to send email');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEsignRequest = async (docId) => {
    if (!signerName.trim() || !signerEmail.trim() || !signerMobile.trim()) {
      toast.warning('All signer fields are required');
      return;
    }
    setActionLoading(true);
    try {
      await apiCall(`${API_BASE}/integrations/esign`, {
        method: 'POST',
        data: {
          document_id: docId,
          signer_name: signerName.trim(),
          signer_email: signerEmail.trim(),
          signer_mobile: signerMobile.trim(),
        },
      });
      toast.success('eSign request initiated!');
      setActiveAction(null);
      setSignerName('');
      setSignerEmail('');
      setSignerMobile('');
      fetchDocuments();
    } catch (err) {
      toast.error(err.message || 'Failed to initiate eSign');
    } finally {
      setActionLoading(false);
    }
  };

  const toggleAction = (docId, type) => {
    if (activeAction?.docId === docId && activeAction?.type === type) {
      setActiveAction(null);
    } else {
      setActiveAction({ docId, type });
      setEmailTo('');
      setSignerName('');
      setSignerEmail('');
      setSignerMobile('');
    }
  };

  // ── Pagination ────────────────────────────────────────────────────────

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  // ── Format date ───────────────────────────────────────────────────────

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mb-3" />
        <p className="text-sm">Loading documents…</p>
      </div>
    );
  }

  if (!loading && documents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-400">
        <svg className="w-16 h-16 mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
        <p className="text-sm font-medium text-gray-500 mb-1">No documents yet</p>
        <p className="text-xs text-gray-400">
          Generated documents will appear here. Use the Templates tab to create one.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Table */}
      <div className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Document</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Source</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Created</th>
              {isAdmin && <th className="text-left px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Client</th>}
              <th className="text-right px-4 py-3 font-semibold text-gray-600 text-xs uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {documents.map((doc) => {
              const src = sourceBadge[doc.source] || sourceBadge.ui;
              const statusClass = statusColors[doc.status] || statusColors.Generated;
              const isActionOpen = activeAction?.docId === doc.id;

              return (
                <React.Fragment key={doc.id}>
                  <tr className="hover:bg-gray-50/50 transition-colors">
                    {/* Document name */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900 truncate max-w-xs" title={doc.pdf_filename}>
                        {doc.template_name || doc.pdf_filename || 'Untitled'}
                      </div>
                      {doc.order_id && (
                        <span className="text-xs text-gray-400">Order: {doc.order_id}</span>
                      )}
                    </td>

                    {/* Source */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${src.color}`}>
                        {src.label}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                        {doc.status || 'Generated'}
                      </span>
                    </td>

                    {/* Created */}
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {formatDate(doc.created_at)}
                    </td>

                    {/* Client (admin only) */}
                    {isAdmin && (
                      <td className="px-4 py-3 text-gray-500 text-xs">
                        {doc.client_id || '—'}
                      </td>
                    )}

                    {/* Actions */}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {/* Download PDF */}
                        <button
                          onClick={() => handleDownload(doc.id, 'pdf')}
                          className="p-1.5 rounded-lg text-gray-500 hover:text-red-600 hover:bg-red-50 transition"
                          title="Download PDF"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                          </svg>
                        </button>

                        {/* Download DOCX (only for HTML/word templates, not PDF overlays) */}
                        {doc.template_type !== 'pdf_overlay' && (
                          <button
                            onClick={() => handleDownload(doc.id, 'docx')}
                            className="p-1.5 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition"
                            title="Download Word (.docx)"
                          >
                            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M7 21h10a2 2 0 002-2V9l-5-5H7a2 2 0 00-2 2v13a2 2 0 002 2z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 4v5h5" />
                              <text x="7.5" y="17.5" fontSize="7" fontWeight="bold" fill="currentColor" stroke="none">W</text>
                            </svg>
                          </button>
                        )}

                        {/* Email */}
                        <button
                          onClick={() => toggleAction(doc.id, 'email')}
                          className={`p-1.5 rounded-lg transition ${
                            isActionOpen && activeAction.type === 'email'
                              ? 'text-blue-600 bg-blue-50'
                              : 'text-gray-500 hover:text-blue-600 hover:bg-blue-50'
                          }`}
                          title="Email Document"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </button>

                        {/* eSign */}
                        <button
                          onClick={() => toggleAction(doc.id, 'esign')}
                          className={`p-1.5 rounded-lg transition ${
                            isActionOpen && activeAction.type === 'esign'
                              ? 'text-purple-600 bg-purple-50'
                              : 'text-gray-500 hover:text-purple-600 hover:bg-purple-50'
                          }`}
                          title="Request eSign"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                              d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>

                  {/* Inline Email Form */}
                  {isActionOpen && activeAction.type === 'email' && (
                    <tr>
                      <td colSpan={isAdmin ? 6 : 5} className="px-4 py-3 bg-blue-50/50">
                        <div className="flex items-center gap-2 max-w-md">
                          <input
                            type="email"
                            value={emailTo}
                            onChange={(e) => setEmailTo(e.target.value)}
                            placeholder="recipient@example.com"
                            className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                          />
                          <button
                            onClick={() => handleEmailSend(doc.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
                          >
                            {actionLoading ? 'Sending…' : 'Send'}
                          </button>
                          <button
                            onClick={() => setActiveAction(null)}
                            className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Inline eSign Form */}
                  {isActionOpen && activeAction.type === 'esign' && (
                    <tr>
                      <td colSpan={isAdmin ? 6 : 5} className="px-4 py-3 bg-purple-50/50">
                        <div className="flex flex-wrap items-center gap-2 max-w-2xl">
                          <input
                            value={signerName}
                            onChange={(e) => setSignerName(e.target.value)}
                            placeholder="Signer Name"
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none w-40"
                          />
                          <input
                            type="email"
                            value={signerEmail}
                            onChange={(e) => setSignerEmail(e.target.value)}
                            placeholder="Email"
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none w-44"
                          />
                          <input
                            type="tel"
                            value={signerMobile}
                            onChange={(e) => setSignerMobile(e.target.value)}
                            placeholder="Mobile (10 digits)"
                            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none w-36"
                          />
                          <button
                            onClick={() => handleEsignRequest(doc.id)}
                            disabled={actionLoading}
                            className="px-3 py-1.5 text-xs font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
                          >
                            {actionLoading ? 'Sending…' : 'Request'}
                          </button>
                          <button
                            onClick={() => setActiveAction(null)}
                            className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 px-1">
          <p className="text-xs text-gray-500">
            Showing {offset + 1}–{Math.min(offset + PAGE_SIZE, total)} of {total} documents
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
              disabled={offset === 0}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-xs text-gray-500 px-2">
              Page {currentPage} of {totalPages}
            </span>
            <button
              onClick={() => setOffset(offset + PAGE_SIZE)}
              disabled={offset + PAGE_SIZE >= total}
              className="px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentList;
